import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { FileText, FileEdit, Send, CheckCircle, XCircle, TrendingUp, ArrowUp, ArrowDown, Minus, ChevronRight } from 'lucide-react';
import { formatRelativeShort } from '../utils/helpers';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { DateRangePicker } from 'rsuite';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import AnalisisIA from './AnalisisIA';
import Button from './ui/Button';
import Badge from './ui/Badge';

// Registrar componentes de Chart.js
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const Dashboard = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { tienePermiso, user } = useAuth();
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    // Colores del chart adaptados al tema
    const axisColor       = isDark ? 'rgba(255,255,255,0.5)'   : 'rgba(15,23,42,0.6)';
    const gridColor       = isDark ? 'rgba(255,255,255,0.05)'  : 'rgba(15,23,42,0.08)';
    const tooltipBg       = isDark ? 'rgba(20,30,60,0.95)'     : 'rgba(255,255,255,0.98)';
    const tooltipText     = isDark ? '#fff'                    : '#0f172a';
    const tooltipBody     = isDark ? 'rgba(255,255,255,0.8)'   : 'rgba(15,23,42,0.75)';
    const tooltipBorder   = isDark ? 'rgba(140,220,255,0.3)'   : 'rgba(15,23,42,0.12)';
    const verPrecios = tienePermiso('ver_detalles_costos');
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [filtroRango, setFiltroRango] = useState('30'); // 7, 30, custom
    const [dateRange, setDateRange] = useState(null);
    const chartRef = useRef(null);
    const [hiddenDatasets, setHiddenDatasets] = useState(new Set());

    useEffect(() => {
        handleFiltroChange('30');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (location.hash === '#analisis-ia') {
            setTimeout(() => {
                const el = document.getElementById('analisis-ia');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 200);
        }
    }, [location.hash]);

    const cargarStats = async (desde = null, hasta = null) => {
        setLoading(true);
        try {
            const params = {};
            if (desde) params.desde = desde;
            if (hasta) params.hasta = hasta;

            const response = await api.dashboard.getStats(params);
            setStats(response);
        } catch (err) {
            console.error('Error al cargar estadísticas:', err);
            toast.error('Error al cargar estadísticas');
        } finally {
            setLoading(false);
        }
    };

    const handleFiltroChange = (rango) => {
        setFiltroRango(rango);

        if (rango === 'custom') return;

        const hoy = new Date();
        const desde = new Date();
        desde.setDate(hoy.getDate() - parseInt(rango));

        setDateRange([desde, hoy]);
        const desdeStr = desde.toISOString().split('T')[0];
        const hastaStr = hoy.toISOString().split('T')[0];
        cargarStats(desdeStr, hastaStr);
    };

    const handleDateRangeChange = (value) => {
        setDateRange(value);
        if (value && value[0] && value[1]) {
            const desdeStr = value[0].toISOString().split('T')[0];
            const hastaStr = value[1].toISOString().split('T')[0];
            cargarStats(desdeStr, hastaStr);
        }
    };

    const formatCurrency = (amount, moneda = 'PEN') => {
        const symbol = moneda === 'USD' ? '$ ' : 'S/ ';
        return symbol + parseFloat(amount || 0).toLocaleString('es-PE', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const priceClass = verPrecios ? '' : 'price-hidden';
    const blurPrice = (value, moneda) => verPrecios ? formatCurrency(value, moneda) : '•••••';

    const formatDate = (dateStr) => {
        const [y, m, d] = dateStr.split('-');
        return `${d}/${m}`;
    };

    // Generar historial con todas las fechas del rango (sin saltos)
    const generarHistorialCompleto = () => {
        if (!stats?.historial) return [];

        let startDate, endDate;
        if (dateRange && dateRange[0] && dateRange[1]) {
            startDate = new Date(dateRange[0]);
            endDate = new Date(dateRange[1]);
        } else if (stats.historial.length > 0) {
            const fechas = stats.historial.map(h => new Date(h.fecha + 'T00:00:00'));
            startDate = new Date(Math.min(...fechas));
            endDate = new Date(Math.max(...fechas));
        } else {
            return [];
        }

        startDate.setHours(0, 0, 0, 0);
        endDate.setHours(0, 0, 0, 0);

        // Mapa de datos existentes por fecha YYYY-MM-DD
        const dataMap = {};
        stats.historial.forEach(h => {
            const key = typeof h.fecha === 'string' ? h.fecha.split('T')[0] : new Date(h.fecha).toISOString().split('T')[0];
            dataMap[key] = h;
        });

        // Generar todas las fechas en el rango
        const result = [];
        const current = new Date(startDate);
        while (current <= endDate) {
            const key = current.toISOString().split('T')[0];
            result.push(dataMap[key] || { fecha: key, total: 0, aprobadas: 0, enviadas: 0 });
            current.setDate(current.getDate() + 1);
        }

        return result;
    };

    const historialCompleto = generarHistorialCompleto();

    // Paleta de 3 hues bien separados en el circulo cromatico (azul / verde /
    // ambar) para que cada linea se distinga sin esfuerzo.
    //   total → azul (la metrica principal)
    //   aprobadas → verde (semantica: aprobado = OK)
    //   enviadas → ambar (en espera de respuesta, no compite con azul/verde)
    const legendColors = isDark
        ? { total: '#60A5FA', aprobadas: '#34D399', enviadas: '#FBBF24' }
        : { total: '#2563EB', aprobadas: '#059669', enviadas: '#D97706' };
    const legendItems = [
        { label: 'Total', color: legendColors.total, datasetIndex: 0 },
        { label: 'Aprobadas', color: legendColors.aprobadas, datasetIndex: 1 },
        { label: 'Enviadas', color: legendColors.enviadas, datasetIndex: 2 }
    ];

    const toggleDataset = (index) => {
        const chart = chartRef.current;
        if (!chart) return;
        const meta = chart.getDatasetMeta(index);
        meta.hidden = !meta.hidden;
        chart.update();
        setHiddenDatasets(prev => {
            const next = new Set(prev);
            if (next.has(index)) next.delete(index);
            else next.add(index);
            return next;
        });
    };

    // Misma paleta que la leyenda (azul / verde / ambar)
    const seriesColors = useMemo(() => isDark
        ? { total: '#60A5FA', aprobadas: '#34D399', enviadas: '#FBBF24' }
        : { total: '#2563EB', aprobadas: '#059669', enviadas: '#D97706' },
        [isDark]
    );

    // Configuración del gráfico
    const chartData = {
        labels: historialCompleto.map(h => formatDate(typeof h.fecha === 'string' ? h.fecha.split('T')[0] : new Date(h.fecha).toISOString().split('T')[0])),
        datasets: [
            {
                // Total: area rellena cyan (es la "envolvente" — la suma)
                label: 'Total',
                data: historialCompleto.map(h => h.total),
                borderColor: seriesColors.total,
                backgroundColor: seriesColors.total + '1A', // 10% opacity para que las lineas internas se vean
                pointBackgroundColor: seriesColors.total,
                pointBorderColor: '#ffffff',
                pointBorderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 6,
                borderWidth: 3,
                fill: true,
                tension: 0.4
            },
            {
                // Aprobadas: linea solida verde, sin fill (para no tapar la de Total)
                label: 'Aprobadas',
                data: historialCompleto.map(h => h.aprobadas),
                borderColor: seriesColors.aprobadas,
                backgroundColor: seriesColors.aprobadas,
                pointBackgroundColor: seriesColors.aprobadas,
                pointBorderColor: '#ffffff',
                pointBorderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 6,
                borderWidth: 3,
                fill: false,
                tension: 0.4
            },
            {
                // Enviadas: linea dasheada ambar para distinguir de Aprobadas a primera vista
                label: 'Enviadas',
                data: historialCompleto.map(h => h.enviadas),
                borderColor: seriesColors.enviadas,
                backgroundColor: seriesColors.enviadas,
                pointBackgroundColor: seriesColors.enviadas,
                pointBorderColor: '#ffffff',
                pointBorderWidth: 2,
                pointRadius: 4,
                pointHoverRadius: 6,
                borderWidth: 3,
                borderDash: [6, 4],
                fill: false,
                tension: 0.4
            }
        ]
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                backgroundColor: tooltipBg,
                titleColor: tooltipText,
                bodyColor: tooltipBody,
                borderColor: tooltipBorder,
                borderWidth: 1,
                padding: 12,
                cornerRadius: 8
            }
        },
        scales: {
            x: {
                grid: {
                    color: gridColor
                },
                ticks: {
                    color: axisColor,
                    maxTicksLimit: 15,
                    maxRotation: 0
                }
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: gridColor
                },
                ticks: {
                    color: axisColor,
                    stepSize: 1
                }
            }
        }
    };

    if (loading && !stats) {
        return <div className="loading">Cargando dashboard...</div>;
    }

    const contadores = stats?.contadores || {};
    const filtroTotales = stats?.filtroTotales || {};

    const hora = new Date().getHours();
    const saludo = hora < 12 ? 'Buenos días' : hora < 19 ? 'Buenas tardes' : 'Buenas noches';
    const fechaHoy = new Date().toLocaleDateString('es-PE', {
        weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });

    // Label corto del periodo segun el filtro activo. Se usa como subcopy en
    // KPIs, banner del monto y header del grafico.
    const formatRangoCorto = (rango) => {
        if (!rango || !rango[0] || !rango[1]) return '';
        const d = (x) => x.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }).replace(/\./g, '');
        return `${d(rango[0])} → ${d(rango[1])}`;
    };
    const periodoLabel = filtroRango === '7'
        ? 'Últimos 7 días'
        : filtroRango === '30'
            ? 'Últimos 30 días'
            : (dateRange ? `Rango ${formatRangoCorto(dateRange)}` : 'Rango personalizado');

    // Comparacion "hoy vs ayer" para el pill del primer KPI.
    // Devuelve un objeto { texto, direccion } o null si no aplica.
    const compararHoyAyer = (hoy, ayer) => {
        const h = Number(hoy || 0);
        const a = Number(ayer || 0);
        if (h === 0 && a === 0) return { texto: 'sin movimiento', direccion: 'flat' };
        if (a === 0) return { texto: `+${h} vs ayer`, direccion: 'up' };
        const diffPct = Math.round(((h - a) / a) * 100);
        if (diffPct === 0) return { texto: '= vs ayer', direccion: 'flat' };
        return {
            texto: `${diffPct > 0 ? '+' : ''}${diffPct}% vs ayer`,
            direccion: diffPct > 0 ? 'up' : 'down'
        };
    };
    const enviadasHoy = Number(contadores.enviadas_hoy || 0);
    const compHoy = compararHoyAyer(contadores.enviadas_hoy, contadores.enviadas_ayer);

    // Hint dinamico para Tasa Conversion: muestra los numeros base para que se
    // entienda que compara (aprobadas / enviadas del mes).
    const aprobadasMes = Number(contadores.aprobadas_mes || 0);
    const enviadasMes = Number(contadores.enviadas_mes || 0);
    const hintTasa = enviadasMes > 0
        ? `${aprobadasMes} de ${enviadasMes} enviadas`
        : 'Sin envíos este mes';

    // KPIs:
    //   - Primero "Enviadas hoy" con pill comparativo vs ayer (refleja actividad diaria del equipo)
    //   - Resto: contadores del MES ACTUAL (created_at en este mes)
    const kpis = [
        { label: 'Enviadas hoy',    value: enviadasHoy,                                         comp: compHoy,                  icon: Send,        color: 'blue'   },
        { label: 'Borradores',      value: Number(contadores.borradores_mes || 0),              hint: 'Este mes',               icon: FileEdit,    color: 'neutral'},
        { label: 'Enviadas',        value: Number(contadores.enviadas_mes || 0),                hint: 'Este mes',               icon: Send,        color: 'blue'   },
        { label: 'Aprobadas',       value: aprobadasMes,                                        hint: 'Este mes',               icon: CheckCircle, color: 'green'  },
        { label: 'Rechazadas',      value: Number(contadores.rechazadas_mes || 0),              hint: 'Este mes',               icon: XCircle,     color: 'red'    },
        { label: 'Tasa Conversión', value: `${contadores.tasaConversionMes || 0}%`,             hint: hintTasa,                 icon: TrendingUp,  color: 'purple' }
    ];

    const colorClasses = {
        cyan:    'bg-cyan2/15 text-cyan2',
        blue:    'bg-primary/15 text-primary',
        green:   'bg-success/15 text-success',
        red:     'bg-danger/15 text-danger',
        purple:  'bg-purple2/15 text-purple2',
        neutral: 'bg-surface-2 text-muted'
    };

    return (
        <div className="dashboard">
            {/* Welcome heading + fecha */}
            <div className="mb-6">
                <p className="text-[11px] font-semibold tracking-wider uppercase text-muted mb-1">
                    {fechaHoy}
                </p>
                <h1 className="text-2xl sm:text-3xl font-bold text-text">
                    {saludo}, <span className="text-primary">{user?.nombre?.split(' ')[0] || 'Usuario'}</span>
                </h1>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
                {kpis.map((kpi) => {
                    const Icon = kpi.icon;
                    // Color del pill comparativo: verde si sube, rojo si baja, muted si flat.
                    const pillColor = kpi.comp
                        ? kpi.comp.direccion === 'up'
                            ? 'text-success'
                            : kpi.comp.direccion === 'down'
                                ? 'text-danger'
                                : 'text-muted'
                        : null;
                    const PillIcon = kpi.comp
                        ? kpi.comp.direccion === 'up' ? ArrowUp
                        : kpi.comp.direccion === 'down' ? ArrowDown
                        : Minus
                        : null;
                    return (
                        <div
                            key={kpi.label}
                            className="bg-surface border border-line rounded-xl p-4 hover:shadow-soft transition"
                        >
                            {/* Icono + Label al costado para ahorrar espacio vertical */}
                            <div className="flex items-center gap-2 mb-2">
                                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0 ${colorClasses[kpi.color]}`}>
                                    <Icon size={16} />
                                </span>
                                <p className="text-[11px] font-medium uppercase tracking-wide text-muted truncate">{kpi.label}</p>
                            </div>
                            {/* pl-10 = w-8 (32px) + gap-2 (8px) para que valor y hint queden
                                alineados con el label de arriba, no con el borde del card. */}
                            <div className="pl-10">
                                <p className="text-2xl font-bold text-text leading-none">{kpi.value}</p>
                                {kpi.comp ? (
                                    <p className={`text-[11px] mt-1.5 inline-flex items-center gap-1 ${pillColor}`} title={kpi.comp.texto}>
                                        <PillIcon size={12} />
                                        <span className="truncate">{kpi.comp.texto}</span>
                                    </p>
                                ) : kpi.hint ? (
                                    <p className="text-[11px] text-muted mt-1.5 truncate" title={kpi.hint}>{kpi.hint}</p>
                                ) : null}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Monto aprobado del mes actual (cotizaciones creadas + aprobadas en este mes calendario) */}
            <div className="bg-gradient-to-r from-success/10 to-success/5 border border-success/30 rounded-xl px-6 py-4 mb-6 flex items-center justify-between flex-wrap gap-2">
                <div className="flex flex-col">
                    <span className="text-sm font-medium text-muted uppercase tracking-wide">Monto Aprobado del Mes</span>
                    <span className="text-[11px] text-muted mt-0.5">Suma de cotizaciones aprobadas en {new Date().toLocaleDateString('es-PE', { month: 'long', year: 'numeric' })}</span>
                </div>
                <span className={`text-2xl font-bold text-success ${priceClass}`}>{blurPrice(contadores.monto_aprobado_mes)}</span>
            </div>

            {/* Gráfico y tabla */}
            <div className="dashboard-content">
                {/* Gráfico */}
                <div className="dashboard-chart card">
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <div className="flex flex-col">
                            <h3 style={{ margin: 0 }}>Historial de Cotizaciones</h3>
                            <span className="text-[11px] text-muted mt-0.5">
                                {periodoLabel}
                                {dateRange && filtroRango !== 'custom' && ` · ${formatRangoCorto(dateRange)}`}
                            </span>
                        </div>
                        <div className="dashboard-filters">
                            <div className="filter-buttons">
                                <button
                                    className={`filter-btn ${filtroRango === '7' ? 'active' : ''}`}
                                    onClick={() => handleFiltroChange('7')}
                                >
                                    7 días
                                </button>
                                <button
                                    className={`filter-btn ${filtroRango === '30' ? 'active' : ''}`}
                                    onClick={() => handleFiltroChange('30')}
                                >
                                    30 días
                                </button>
                                <button
                                    className={`filter-btn ${filtroRango === 'custom' ? 'active' : ''}`}
                                    onClick={() => setFiltroRango('custom')}
                                >
                                    Personalizado
                                </button>
                            </div>
                            {filtroRango === 'custom' && (
                                <div className="filter-custom">
                                    <DateRangePicker
                                        value={dateRange}
                                        onChange={handleDateRangeChange}
                                        format="dd/MM/yyyy"
                                        character=" - "
                                        placeholder="Seleccionar rango"
                                        showOneCalendar
                                        placement="bottomEnd"
                                        className="rs-theme-dark"
                                        ranges={[]}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="card-body">
                        <div className="chart-legend">
                            {legendItems.map(item => (
                                <div
                                    key={item.datasetIndex}
                                    className={`chart-legend-item ${hiddenDatasets.has(item.datasetIndex) ? 'dimmed' : ''}`}
                                    onClick={() => toggleDataset(item.datasetIndex)}
                                >
                                    <span
                                        className="chart-legend-dot"
                                        style={{ backgroundColor: hiddenDatasets.has(item.datasetIndex) ? 'rgba(100,100,100,0.3)' : item.color }}
                                    />
                                    {item.label}
                                </div>
                            ))}
                        </div>
                        <div className="chart-container">
                            {historialCompleto.length > 0 ? (
                                <Line ref={chartRef} data={chartData} options={chartOptions} />
                            ) : (
                                <div className="empty-state">
                                    <p>No hay datos para el período seleccionado</p>
                                </div>
                            )}
                        </div>

                        {/* Resumen del período filtrado — pills derivados:
                            Total → label del periodo (Ultimos 30 dias / Rango ...)
                            Enviadas → % del total (cuantas de las creadas se llegaron a enviar)
                            Aprobadas → % conversion sobre enviadas
                            Monto Aprobado → ticket promedio (monto / nro aprobadas) */}
                        {(() => {
                            const fTotal = Number(filtroTotales.total) || 0;
                            const fEnv = Number(filtroTotales.enviadas) || 0;
                            const fApr = Number(filtroTotales.aprobadas) || 0;
                            const fMonto = Number(filtroTotales.monto_aprobado) || 0;
                            const fMontoPrev = Number(stats?.filtroPrevio?.monto_aprobado) || 0;
                            const envPct = fTotal > 0 ? Math.round((fEnv / fTotal) * 100) : 0;
                            const conv = fEnv > 0 ? Math.round((fApr / fEnv) * 100) : 0;

                            // Comparativa monto vs periodo anterior (mismo nro de dias hacia atras).
                            // Casos: ambos 0 → sin datos; previo 0 → "Nuevo"; sino, % de cambio con signo.
                            let montoHint;
                            if (fMonto === 0 && fMontoPrev === 0) {
                                montoHint = 'Sin aprobadas';
                            } else if (fMontoPrev === 0) {
                                montoHint = '↑ Nuevo';
                            } else {
                                const deltaPct = Math.round(((fMonto - fMontoPrev) / fMontoPrev) * 100);
                                const arrow = deltaPct > 0 ? '↑' : deltaPct < 0 ? '↓' : '';
                                const sign = deltaPct > 0 ? '+' : '';
                                montoHint = `${arrow} ${sign}${deltaPct}% vs período anterior`.trim();
                            }
                            return (
                                <div className="chart-summary">
                                    <div className="chart-summary-item">
                                        <span className="chart-summary-value" style={{ color: legendColors.total }}>{fTotal}</span>
                                        <span className="chart-summary-label">Total</span>
                                        <span className="chart-summary-hint">{periodoLabel}</span>
                                    </div>
                                    <div className="chart-summary-item">
                                        <span className="chart-summary-value" style={{ color: legendColors.enviadas }}>{fEnv}</span>
                                        <span className="chart-summary-label">Enviadas</span>
                                        <span className="chart-summary-hint">
                                            {fTotal > 0 ? `${envPct}% del total` : 'Sin actividad'}
                                        </span>
                                    </div>
                                    <div className="chart-summary-item">
                                        <span className="chart-summary-value" style={{ color: legendColors.aprobadas }}>{fApr}</span>
                                        <span className="chart-summary-label">Aprobadas</span>
                                        <span className="chart-summary-hint">
                                            {fEnv > 0 ? `${conv}% conversión` : 'Sin enviadas'}
                                        </span>
                                    </div>
                                    <div className="chart-summary-item">
                                        <span className={`chart-summary-value monto ${priceClass}`} style={{ color: legendColors.aprobadas }}>{blurPrice(fMonto)}</span>
                                        <span className="chart-summary-label">Monto Aprobado</span>
                                        <span className="chart-summary-hint">{montoHint}</span>
                                    </div>
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {/* Últimas cotizaciones */}
                <div className="dashboard-recent card">
                    <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div className="flex flex-col">
                            <h3 style={{ margin: 0 }}>Últimas Cotizaciones</h3>
                            <span className="text-[11px] text-muted mt-0.5">
                                {stats?.ultimas?.length > 0
                                    ? `${stats.ultimas.length} más recientes`
                                    : 'Sin actividad reciente'}
                            </span>
                        </div>
                        <Button
                            variant="soft"
                            size="sm"
                            rightIcon={<ChevronRight size={14} />}
                            onClick={() => navigate('/cotizaciones')}
                        >
                            Ver todas
                        </Button>
                    </div>
                    <div className="card-body">
                        {stats?.ultimas?.length > 0 ? (
                            <div className="recent-list">
                                {stats.ultimas.map((cot) => {
                                    // Borrador → form editable. Otros → detalle publico.
                                    const destino = cot.estado === 'borrador'
                                        ? `/cotizaciones/${cot.id}`
                                        : `/cotizaciones/${cot.id}/detalle`;
                                    // Color del badge segun estado, coherente con paleta de la app.
                                    const badgeColor = cot.estado === 'aprobada' ? 'success'
                                        : cot.estado === 'rechazada' ? 'danger'
                                        : cot.estado === 'enviada' ? 'primary'
                                        : 'neutral';
                                    const fechaRelativa = formatRelativeShort(cot.created_at);
                                    return (
                                        <div
                                            key={cot.id}
                                            className="recent-item"
                                            onClick={() => navigate(destino)}
                                        >
                                            <div className="recent-info">
                                                <span className="recent-numero">{cot.numero}</span>
                                                <span className="recent-cliente">
                                                    {cot.cliente_nombre}
                                                    {fechaRelativa && (
                                                        <span className="recent-fecha"> · {fechaRelativa}</span>
                                                    )}
                                                </span>
                                            </div>
                                            <div className="recent-meta">
                                                {/* Inline style para garantizar font-size, padding y ancho
                                                    uniforme. Tailwind purge a veces ignora valores arbitrarios
                                                    como text-[10px] del primitivo Badge en size="sm". */}
                                                <Badge
                                                    color={badgeColor}
                                                    size="sm"
                                                    style={{
                                                        minWidth: 72,
                                                        justifyContent: 'center',
                                                        fontSize: 10,
                                                        padding: '2px 8px',
                                                        letterSpacing: '0.4px'
                                                    }}
                                                >
                                                    {cot.estado}
                                                </Badge>
                                                <span className={`recent-total ${priceClass}`}>
                                                    {blurPrice(cot.total, cot.moneda)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="empty-state">
                                <p>No hay cotizaciones recientes</p>
                            </div>
                        )}
                    </div>
                </div>

                <div id="analisis-ia">
                    <AnalisisIA />
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
